import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../Button/Button'
import { EmptyState, Skeleton } from '../EmptyState/EmptyState'
import { StatusChip } from '../StatusChip/StatusChip'
import { SuccessBlock } from '../SuccessBlock/SuccessBlock'
import { cx } from '../utils/cx'
import './BankAuthorization.css'

export type BankAuthState = 'starting' | 'waiting' | 'authorized' | 'rejected' | 'timeout'

export interface BankAuthorizationProps {
  state: BankAuthState
  /** URL del banco (E3 `paidRedirectURL`); solo se usa en `waiting` */
  src?: string | null
  /** Importe ya formateado */
  amount: ReactNode
  /** Texto del estado autorizado; por defecto "Se ha completado el pago" (app actual) */
  authorizedTitle?: ReactNode
  authorizedText?: ReactNode
  rejectedText?: ReactNode
  onContinue?: () => void
  continueLabel?: ReactNode
  onRetry?: () => void
  onCheckAgain?: () => void
  /** Deja de esperar sin cancelar nada en el banco (lleva al estado de tiempo agotado) */
  onStopWaiting?: () => void
  checking?: boolean
  /** Acciones secundarias bajo el estado (p. ej. "Pagar otra cantidad", contacto) */
  children?: ReactNode
  className?: string
}

/**
 * Autorización del pago en el banco (CLAUDE.md, inventario §4.1 y D34). El iframe del banco NO se estiliza:
 * se muestra con el mismo `sandbox` que la app actual y sin postMessage. Lo que se rehace son sus estados
 * alrededor, con componentes del sistema: conectando, esperando autorización (con aria-live), autorizado,
 * rechazado y tiempo agotado. El bundle no dibuja estos estados: textos de la app actual o interpretación
 * (docs/migracion/bloque-3-alcance.md).
 */
export function BankAuthorization({ state, src, amount, authorizedTitle, authorizedText, rejectedText, onContinue, continueLabel, onRetry, onCheckAgain, onStopWaiting, checking, children, className }: BankAuthorizationProps) {
  const { t } = useTranslation()
  return (
    <div className={cx('hp-bankauth', `hp-bankauth--${state}`, className)}>
      <div className="hp-bankauth__live" aria-live="polite">
        {state === 'starting' && <p className="hp-bankauth__status">{t('v2.ds.bankAuth.conectando')}</p>}
        {state === 'waiting' && (
          <div className="hp-bankauth__waiting">
            <StatusChip tone="warning">{t('v2.ds.bankAuth.esperando')}</StatusChip>
            <p className="hp-bankauth__status">{t('v2.ds.bankAuth.esperandoTexto', { amount })}</p>
          </div>
        )}
        {state === 'authorized' && (
          <SuccessBlock title={authorizedTitle ?? t('v2.legacy.payment.Payment_successful')} amount={amount} action={onContinue && <Button onClick={onContinue}>{continueLabel ?? t('v2.ds.bankAuth.continuar')}</Button>}>
            {authorizedText}
          </SuccessBlock>
        )}
        {state === 'rejected' && (
          <EmptyState tone="error" icon="x-circle" title={t('v2.legacy.payment.error.Payment_failed.title')} headingLevel={2} actionLabel={t('v2.comun.reintentar')} {...(onRetry ? { onAction: onRetry } : {})}>
            {rejectedText ?? t('v2.legacy.payment.error.Payment_failed.description')}
          </EmptyState>
        )}
        {state === 'timeout' && (
          <EmptyState tone="neutral" icon="clock" title={t('v2.ds.bankAuth.sinRespuesta')} headingLevel={2}>
            {t('v2.ds.bankAuth.sinRespuestaTexto')}
          </EmptyState>
        )}
      </div>

      {state === 'starting' && (
        <div aria-busy="true">
          <Skeleton height={420} />
        </div>
      )}
      {state === 'waiting' && src && (
        <iframe className="hp-bankauth__frame" src={src} title={t('v2.ds.bankAuth.ventanaBanco')} sandbox="allow-same-origin allow-scripts allow-popups allow-forms" />
      )}
      {state === 'waiting' && onStopWaiting && (
        <Button variant="tertiary" onClick={onStopWaiting}>
          {t('v2.ds.bankAuth.dejarDeEsperar')}
        </Button>
      )}
      {state === 'timeout' && onCheckAgain && (
        <Button icon="refresh-cw" onClick={onCheckAgain} loading={checking ?? false}>
          {t('v2.ds.bankAuth.comprobar')}
        </Button>
      )}
      {children && <div className="hp-bankauth__extra">{children}</div>}
    </div>
  )
}
